import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { MessageSquare, Wrench, BookOpen, Bookmark, ArrowRight, Flame, Quote, Heart, MapPin, HeartCrack } from "lucide-react";
import { motion } from "framer-motion";
import DailyLessonCard from "@/components/DailyLessonCard";
import WeeklyCheckInCard from "@/components/WeeklyCheckInCard";

const IMG = "https://images.unsplash.com/photo-1780337092500-f59e1366e629?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBtaW5pbWFsaXN0JTIwbGlmZXN0eWxlJTIwbWVuJTIwZGFya3xlbnwwfHx8fDE3ODM2NTY4Mzd8MA&ixlib=rb-4.1.0&q=85";

const DAILY_QUOTES = [
  { q: "Attention is currency. Notice the small things.", by: "Suitor" },
  { q: "You act, gather evidence, and belief follows.", by: "Suitor" },
  { q: "Momentum kills obsession.", by: "Suitor" },
  { q: "Believe what you see. Not what you're told.", by: "Suitor" },
];

const Dashboard = () => {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [favs, setFavs] = useState([]);
  const quote = DAILY_QUOTES[new Date().getDate() % DAILY_QUOTES.length];

  useEffect(() => {
    api.get("/chat/sessions").then((r) => setSessions(r.data || [])).catch(() => setSessions([]));
    api.get("/favorites").then((r) => setFavs(r.data.favorites || [])).catch(() => {});
  }, []);

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <div className="overline mb-2">DASHBOARD</div>
        <h1 className="font-serif text-4xl sm:text-5xl mb-2" data-testid="dashboard-title">
          Welcome, <span className="italic text-amber-500">{user?.name}</span>.
        </h1>
        <p className="text-neutral-400 max-w-xl">Your dating & relationships playbook. Pick up where you left off.</p>
      </motion.div>

      {/* Daily Lesson */}
      <div className="mt-10">
        <DailyLessonCard />
      </div>

      {/* Weekly check-in */}
      <div className="mt-6">
        <WeeklyCheckInCard />
      </div>

      {/* Quote Card */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mt-6 card-surface p-8 md:p-10 relative overflow-hidden"
        data-testid="daily-quote"
      >
        <img src={IMG} alt="lifestyle" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="relative">
          <Quote className="w-6 h-6 text-amber-500 mb-3" />
          <p className="font-serif text-2xl md:text-3xl leading-snug max-w-3xl">"{quote.q}"</p>
          <p className="overline mt-4 text-neutral-500">— {quote.by}</p>
        </div>
      </motion.div>

      {/* Quick actions */}
      <div className="grid md:grid-cols-2 gap-6 mt-10">
        <Link to="/coach" className="card-surface p-8 hover-lift" data-testid="qa-coach">
          <MessageSquare className="w-8 h-8 text-amber-500 mb-4"/>
          <h3 className="font-serif text-2xl mb-2">Ask the Coach</h3>
          <p className="text-neutral-400">Get personalised advice on any dating situation.</p>
          <div className="mt-6 inline-flex items-center gap-2 text-amber-500 text-sm">Start a session <ArrowRight className="w-4 h-4"/></div>
        </Link>
        <Link to="/playbook/first-date" className="card-surface p-8 hover-lift" data-testid="qa-playbook">
          <Heart className="w-8 h-8 text-amber-500 mb-4"/>
          <h3 className="font-serif text-2xl mb-2">First-Date Playbook</h3>
          <p className="text-neutral-400">Step-by-step: prep, venue, questions, the kiss moment, follow-up.</p>
          <div className="mt-6 inline-flex items-center gap-2 text-amber-500 text-sm">Open the playbook <ArrowRight className="w-4 h-4"/></div>
        </Link>
        <Link to="/date-plan" className="card-surface p-8 hover-lift" data-testid="qa-date-plan">
          <MapPin className="w-8 h-8 text-amber-500 mb-4"/>
          <h3 className="font-serif text-2xl mb-2">Date Night Planner</h3>
          <p className="text-neutral-400">Pick a city, get a real two-spot date with map & booking links.</p>
          <div className="mt-6 inline-flex items-center gap-2 text-amber-500 text-sm">Plan a date <ArrowRight className="w-4 h-4"/></div>
        </Link>
        <Link to="/recovery" className="card-surface p-8 hover-lift" data-testid="qa-recovery">
          <HeartCrack className="w-8 h-8 text-amber-500 mb-4"/>
          <h3 className="font-serif text-2xl mb-2">Breakup Recovery</h3>
          <p className="text-neutral-400">30-day no-contact program with daily rebuilding tasks and reconnect messages.</p>
          <div className="mt-6 inline-flex items-center gap-2 text-amber-500 text-sm">Start the 30 days <ArrowRight className="w-4 h-4"/></div>
        </Link>
        <Link to="/tools" className="card-surface p-8 hover-lift" data-testid="qa-tools">
          <Wrench className="w-8 h-8 text-amber-500 mb-4"/>
          <h3 className="font-serif text-2xl mb-2">Interactive Tools</h3>
          <p className="text-neutral-400">Text reply generator, opener suggestions, situation analyzer.</p>
          <div className="mt-6 inline-flex items-center gap-2 text-amber-500 text-sm">Open tools <ArrowRight className="w-4 h-4"/></div>
        </Link>
        <Link to="/library" className="card-surface p-8 hover-lift" data-testid="qa-library">
          <BookOpen className="w-8 h-8 text-amber-500 mb-4"/>
          <h3 className="font-serif text-2xl mb-2">The Library</h3>
          <p className="text-neutral-400">Approach, texting, ghosting, first dates, long-term love.</p>
          <div className="mt-6 inline-flex items-center gap-2 text-amber-500 text-sm">Browse chapters <ArrowRight className="w-4 h-4"/></div>
        </Link>
      </div>

      {/* Recent sessions */}
      <div className="mt-14">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-serif text-2xl">Recent conversations</h2>
          <Link to="/coach" className="text-sm text-amber-500 hover:underline" data-testid="see-all-chats">New session</Link>
        </div>
        {sessions.length === 0 ? (
          <div className="card-surface p-8 text-neutral-500" data-testid="no-sessions">
            No conversations yet. <Link to="/coach" className="text-amber-500">Start your first one.</Link>
          </div>
        ) : (
          <div className="space-y-3">
            {sessions.slice(0, 5).map((s) => (
              <Link key={s.session_id} to={`/coach?s=${s.session_id}`} className="block card-surface p-5 hover-lift" data-testid={`chat-item-${s.session_id}`}>
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="font-serif text-lg text-white truncate">{s.title}</div>
                    <div className="text-xs text-neutral-500 mt-1">{new Date(s.updated_at).toLocaleString()}</div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-neutral-500 shrink-0" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
